import * as React from 'react';
import { MapPin, Phone, Star, Euro, Check, Award } from 'lucide-react';
import { useClerkUserImage } from '@/hooks/use-clerk-user-image';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import i18n from '@/i18n';
import type { ConfirmedSubstituteCardProps } from '@/types/provider-components';

const fmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * Generate initials from first and last name
 */
const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.trim()?.[0]?.toUpperCase() || '';
  const last = lastName?.trim()?.[0]?.toUpperCase() || '';
  return first + last || '?';
};

/**
 * StarRating - Compact star rating display
 */
const StarRating = ({ rating, showLabel = true }: { rating: number; showLabel?: boolean }): React.JSX.Element => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className='flex items-center gap-1'>
      <div className='flex items-center'>
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={cn('size-4', i < fullStars ? 'text-warning fill-warning' : i === fullStars && hasHalfStar ? 'text-warning fill-warning/50' : 'text-muted-foreground/30')} />
        ))}
      </div>
      {showLabel && <span className='text-sm font-medium text-foreground ml-1'>({rating.toFixed(1)})</span>}
    </div>
  );
};

/**
 * ConfirmedSubstituteCard - Premium card displaying confirmed substitute information
 *
 * Features:
 * - Avatar with Clerk image integration
 * - Star rating display
 * - Specialization badges
 * - Compensation breakdown
 * - Notfallscheinbeteiligung indicator
 * - Elevated card styling with success accent
 */
export const ConfirmedSubstituteCard = ({ applicant, compensation, duty, showConfirmedBadge = true, className }: ConfirmedSubstituteCardProps): React.JSX.Element | null => {
  const clerkImage = useClerkUserImage(applicant?.clerkId);

  if (!applicant) return null;

  // Build full name with title
  const nameParts = [];
  if (applicant.title) nameParts.push(applicant.title);
  if (applicant.firstName) nameParts.push(applicant.firstName);
  if (applicant.lastName) nameParts.push(applicant.lastName);
  const fullName = nameParts.length > 0 ? nameParts.join(' ') : applicant.name || applicant.email || 'Vertreter';

  const initials = getInitials(applicant.firstName, applicant.lastName);

  // Calculate hours and total price
  const hours = duty && duty.startDate && duty.endDate ? (duty.endDate - duty.startDate) / (1000 * 60 * 60) : 0;
  const totalPrice = hours && compensation ? hours * compensation : 0;

  const rating = applicant.rating ?? 0;

  return (
    <Card className={cn('overflow-hidden border-2 border-success/30 bg-success/5', 'shadow-sm hover:shadow-md transition-all duration-200', className)}>
      <div className='p-4 lg:p-5'>
        <div className='flex items-start gap-4'>
          {/* Avatar */}
          <Avatar className='size-14 lg:size-16 shrink-0 ring-2 ring-success/20'>
            <AvatarImage src={clerkImage || applicant.image || ''} alt={fullName} />
            <AvatarFallback className='text-lg font-semibold bg-success/10 text-success'>{initials}</AvatarFallback>
          </Avatar>

          {/* Info */}
          <div className='flex-1 min-w-0 space-y-2'>
            {/* Name and confirmed badge */}
            <div className='flex items-start justify-between gap-2'>
              <div className='flex-1 min-w-0'>
                <h3 className='text-lg font-semibold text-foreground truncate'>{fullName}</h3>
                {rating > 0 && <StarRating rating={rating} />}
              </div>
              {showConfirmedBadge && (
                <Badge className='bg-success/20 text-success border-success/30 shrink-0'>
                  <Check className='size-3 mr-1' />
                  {i18n.t('duties:detail.confirmed')}
                </Badge>
              )}
            </div>

            {/* Specialization badges */}
            {applicant.specialization && applicant.specialization.length > 0 && (
              <div className='flex flex-wrap gap-1'>
                {applicant.specialization.slice(0, 3).map((spec, idx) => (
                  <Badge key={idx} variant='secondary' className='bg-primary/10 text-primary border border-primary/20 text-xs'>
                    {spec}
                  </Badge>
                ))}
                {applicant.specialization.length > 3 && (
                  <Badge variant='secondary' className='text-xs'>
                    +{applicant.specialization.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {/* Location and phone */}
            <div className='flex flex-wrap items-center gap-3 text-sm text-muted-foreground'>
              {applicant.city && (
                <span className='flex items-center gap-1'>
                  <MapPin className='size-4' />
                  {applicant.city}
                </span>
              )}
              {applicant.phone && (
                <a href={`tel:${applicant.phone}`} className='flex items-center gap-1 hover:text-foreground transition-colors'>
                  <Phone className='size-4' />
                  {applicant.phone}
                </a>
              )}
            </div>

            {/* Completed services badge */}
            {(applicant.completedServices ?? 0) > 0 && (
              <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                <Award className='size-3' />
                <span>{applicant.completedServices} abgeschlossene Dienste</span>
              </div>
            )}
          </div>
        </div>

        {/* Compensation section */}
        {compensation !== undefined && (
          <div className='mt-4 pt-4 border-t border-success/20 space-y-2'>
            <div className='flex items-center justify-between'>
              <span className='text-sm text-muted-foreground'>Vereinbarte Vergütung</span>
              <span className='text-lg font-bold text-foreground'>{fmt.format(compensation)}/Std</span>
            </div>

            {hours > 0 && (
              <div className='flex items-center justify-between text-sm'>
                <span className='text-muted-foreground'>
                  {hours.toFixed(1).replace('.0', '')}h × {fmt.format(compensation)}
                </span>
                <span className='font-semibold text-foreground'>{fmt.format(totalPrice)}</span>
              </div>
            )}

            {/* Notfallscheinbeteiligung */}
            {duty?.hasNotfallscheinbeteiligung && duty?.notfallscheinbeteiligungRate && (
              <div className='flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20 text-xs'>
                <Euro className='size-3.5 text-primary shrink-0' />
                <span className='text-muted-foreground'>
                  zzgl. Notfallscheinbeteiligung: <span className='font-medium text-primary'>{fmt.format(duty.notfallscheinbeteiligungRate)}/Patient</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
